import { z } from 'zod';

/** Dock order. */
export const APP_IDS = [
  'files',
  'docs',
  'sheets',
  'pdf',
  'image',
  'calculator',
  'cad',
  'video',
  'notes',
  'calendar',
  'kasa',
  'ai',
  'notebook',
  'browser',
] as const;
export type AppId = (typeof APP_IDS)[number];

export const registerInput = z.object({
  name: z.string().trim().min(1).max(80),
  email: z.email().max(254),
  password: z.string().min(8).max(200),
});
export type RegisterInput = z.infer<typeof registerInput>;

export const loginInput = z.object({
  email: z.email().max(254),
  password: z.string().min(1).max(200),
});
export type LoginInput = z.infer<typeof loginInput>;

export interface User {
  id: string;
  name: string;
  email: string;
  /** The shared visitor of a server running without accounts; there is nothing to sign in to or out of. */
  guest?: true;
}

export interface AuthResponse {
  user: User;
  /** Returned for native shells (Tauri desktop/iOS) that cannot rely on cookies. */
  token: string;
}

export const taskInput = z.object({
  text: z.string().trim().min(1).max(500),
});
export const taskPatch = z
  .object({
    text: z.string().trim().min(1).max(500),
    done: z.boolean(),
  })
  .partial();

export interface Task {
  id: string;
  text: string;
  done: boolean;
  createdAt: string;
}

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const clockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const eventInput = z.object({
  date: isoDate,
  time: clockTime.nullable(),
  title: z.string().trim().min(1).max(200),
});
export const eventRangeQuery = z.object({ from: isoDate, to: isoDate });

export interface CalendarEvent {
  id: string;
  date: string;
  time: string | null;
  title: string;
}

export const folderInput = z.object({
  parentId: z.string().uuid().nullable(),
  name: z.string().trim().min(1).max(255),
});
export const renameInput = z.object({ name: z.string().trim().min(1).max(255) });

export interface FileEntry {
  id: string;
  parentId: string | null;
  name: string;
  kind: 'folder' | 'file';
  size: number;
  mime: string | null;
  /** Starts at 1 and grows each time the file's bytes are replaced. */
  version: number;
  createdAt: string;
}

/** File families tools can list across all folders; the server maps each to extensions and MIME types. */
export const DOCUMENT_TYPES = ['pdf', 'word', 'sheet', 'cad', 'image', 'video', 'audio', 'notebook', 'video-project'] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];
export const documentQuery = z.object({ type: z.enum(DOCUMENT_TYPES) });

export const createFileQuery = z.object({
  parent: z.uuid().optional(),
  name: z.string().trim().min(1).max(255),
  mime: z.string().max(200).optional(),
  dedupe: z.enum(['0', '1']).optional(),
});
export const replaceFileQuery = z.object({ mime: z.string().max(200).optional() });

export interface FileLink {
  url: string;
  expiresAt: string;
}

export interface FolderListing {
  folder: { id: string; name: string } | null;
  path: { id: string; name: string }[];
  entries: FileEntry[];
}

export const windowFrame = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
  width: z.number().finite().positive(),
  height: z.number().finite().positive(),
});
export type WindowFrame = z.infer<typeof windowFrame>;

/** What a window is showing, e.g. the document open in the PDF viewer. */
export const appParams = z.object({ fileId: z.uuid().optional(), folderId: z.uuid().optional() });
export type AppParams = z.infer<typeof appParams>;

export const desktopLayout = z.object({
  /** Where each window is drawn, including a maximized or snapped frame. */
  frames: z.partialRecord(z.enum(APP_IDS), windowFrame),
  /** Frame to return to for windows that are maximized or snapped; absent in layouts saved before this existed. */
  restore: z.partialRecord(z.enum(APP_IDS), windowFrame).optional(),
  minimized: z.array(z.enum(APP_IDS)).max(APP_IDS.length).optional(),
  open: z.array(z.enum(APP_IDS)).max(APP_IDS.length),
  params: z.partialRecord(z.enum(APP_IDS), appParams).optional(),
});
export type DesktopLayout = z.infer<typeof desktopLayout>;

export interface ApiError {
  error: string;
  message: string;
}

export * from './docs.ts';
export * from './sheets.ts';
export * from './cad.ts';
export * from './video.ts';
export * from './pdf-annotations.ts';
export * from './ai.ts';
export * from './cloud.ts';
export * from './widgets.ts';
export * from './kasa.ts';
export * from './archive.ts';
export * from './browser.ts';
